import flightSeatMapConfig from '../flight-seatmap-config';
import travellerTypes from 'modules/trip/traveller-types';
import {SEAT_SUITABLE_FOR_UNUSAL_NEEDS} from './amadeus-seat-enum';

export const EVENT_CLICK = 'click';
export const EVENT_SEAT_SELECTED = 'seat_selected';
export const EVENT_SEAT_HOVERED_BEGIN = 'seat_begin_hover';
export const EVENT_SEAT_HOVERED_END = 'seat_end_hover';
export const CLASS_SEAT_WITH_RESTRICTIONS = 'seat-with-restrictions';
export const CLASS_SEAT_CHARGEABLE = 'seat-chargeable';

const DATE_FORMAT = 'YYYY-MM-DD';
const SEAT_CURRENCY_PATH = 'row.deck.segment.root.passengers[0].flights[0].segments[0].services.currency';
const SEATMAP_HTTP_STATUS_NOT_FOUND = 404;

export default function AmadeusSeatmapService(
    $q,
    $window,
    userService,
    cultureService,
    selectedSeatsService,
    amadeusSeatService,
    tripTravellerAllocationFlightService,
    tripTravellerAllocationPassengerService,
    tripFlightPassengerSeatInfoService,
) {
    'ngInject';

    Object.assign(this, {
        createSeatmapCore,
        retrieveSeatMap,
        createRequest,
        createViewRequest,
        integrateClickingOnSeatElement,
        integrateIsSuitableForPassenger,
        selectSegmentAndDeck,
        getPassengers,
        getDeckSwitchers,
        integrateHoveringOnSeatElement,
        showNumberOfEachTravellerOnSelectedSeats,
        deselectOnOpenedSeatMap,
        selectSeatsOnMap,
        markSeatsAllowedToBeSelectedByTravellerWithInfant,
        isAllSeatAllowedByDefaultForTravellerWithInfant,
    });

    function createSeatmapCore(selector, isSelectable, onSeatmapUpdate) {
        const {SeatMapCore} = $window;
        const contactId = userService.getContactId();

        const options = {
            ...flightSeatMapConfig,
            office: userService.getOfficeId(),
            clientId: `KT-${contactId}`,
        };
        const core = SeatMapCore.newDefaultCore(selector, options);

        let currencySign = null;
        patchSeatRenderer(core, isSelectable, (seat, element) => {
            element.setAttribute('data-tippy', '');
            if (amadeusSeatService.isSeatWithFeatures(seat)) {
                element.classList.add('seat-with-feature');
            }
            if (amadeusSeatService.doesSeatHaveRestrictions(seat)) {
                element.classList.add(CLASS_SEAT_WITH_RESTRICTIONS);
            }
            const price = Math.round(seat.getPrice());
            if (!price) {
                element.innerHTML = '';
                return;
            }
            element.classList.add(CLASS_SEAT_CHARGEABLE);
            if (!currencySign) {
                const currency = _.get(seat, SEAT_CURRENCY_PATH);
                currencySign = cultureService.getCurrencySign(currency);
            }
            element.innerHTML = `<span class='seat-with-price'>${currencySign}${price}</span>`;
        });
        if (onSeatmapUpdate) {
            patchOnSeatmapUpdate(core, onSeatmapUpdate);
        }
        return core;
    }

    function patchSeatRenderer(core, isSelectable, customFunction) {
        if (!(core && core.renderers)) {
            return;
        }
        const divRenderer = core.renderers.get('div');
        const oldRenderIntoElement = divRenderer.renderIntoElement;

        divRenderer.renderIntoElement = (seat, container, fire) => {
            container.addEventListener = addEventListener;

            const element = oldRenderIntoElement.call(divRenderer, seat, container, fire);
            customFunction(seat, element);
            return element;
        };

        function addEventListener(event, ...args) {
            if (event === EVENT_CLICK && !isSelectable()) {
                return;
            }
            Object.getPrototypeOf(this).addEventListener.call(this, event, ...args);
        }
    }

    function patchOnSeatmapUpdate(core, customFunction) {
        if (!(core && core.renderers)) {
            return;
        }
        const divRenderer = core.renderers.get('div');
        const oldUpdate = divRenderer.update;

        divRenderer.update = (seat, seatElement, fire) => {
            oldUpdate.call(divRenderer, seat, seatElement, fire);
            customFunction(seat, seatElement);
        };
    }

    function integrateIsSuitableForPassenger(mapController, customFunction) {
        const oldIsSuitableForPassenger = mapController.isSuitableForPassenger;

        mapController.isSuitableForPassenger = (seat, passenger) => {
            return oldIsSuitableForPassenger.call(mapController, seat, passenger)
                && customFunction(seat, passenger);
        };

        return mapController;
    }

    function retrieveSeatMap(seatMapCore, seatMapRequest) {
        const deferred = $q.defer();
        seatMapCore.retrieve(seatMapRequest)
            .then((data) => deferred.resolve(data))
            .catch((data) => {
                const isNotFound = data
                    && data.response
                    && data.response.status === SEATMAP_HTTP_STATUS_NOT_FOUND;
                if (isNotFound) {
                    deferred.reject({isSeatmapAvailable: false});
                } else {
                    deferred.reject();
                }
            });
        return deferred.promise;
    }

    function createRequest(flight, segmentNumber, tripDetails, tierList, selectedSeats) {
        const {RetrieveRequestBuilder, PassengerType} = $window;

        const PASSENGER_TYPE_MAPPING = {
            [travellerTypes.ADULT]: PassengerType.Adult,
            [travellerTypes.CHILD]: PassengerType.Child,
            [travellerTypes.INFANT]: PassengerType.Infant,
        };

        const marketingCode = flight.MarketingCompanyCode || flight.MarketingAirlineCode;
        const flightNumber = parseFlightNumber(flight.FlightNumber, marketingCode);
        if (!flightNumber) {
            return;
        }

        const departureDate = moment(flight.DepartureDate).format(DATE_FORMAT);
        const arrivalAirportCode = flight.ArrivalAirportCode || flight.ArrivalAirport;
        const departureAirportCode = flight.DepartureAirportCode || flight.DepartureAirport;

        let builder = new RetrieveRequestBuilder(flightNumber);
        const passengerInfo = Object.values(selectedSeats[flight.Id]);
        const travellers = tripFlightPassengerSeatInfoService.associateSeatsWithTravellers(
            tripDetails.Travelers,
            passengerInfo
        );

        travellers.forEach((selectedSeat) => {
            builder = builder.withPassenger(selectedSeat.traveller.FirstName, selectedSeat.traveller.LastName);
            if (selectedSeat.Ffn) {
                const tier = amadeusSeatService.getTier(flight, selectedSeat, tierList);
                builder = builder.withFrequentFlyerData(selectedSeat.SupplierCode, selectedSeat.Ffn, tier);
            }
            builder = builder.ofType(PASSENGER_TYPE_MAPPING[selectedSeat.traveller.PassengerType]);
            builder = withFlight(builder);

            const infant = tripTravellerAllocationPassengerService.getInfantThisPassengerTravellingWith(
                {Id: selectedSeat.TripPassengerId},
                tripDetails
            );
            if (infant) {
                builder = witInfant(builder, infant);
            }
            builder = builder.ok();
        });

        function withFlight(rb) {
            return rb.withFlight()
                .withSegment(departureAirportCode, arrivalAirportCode)
                    .withFareBase(flight.FareBasis)
                    .bookedWithClass(flight.BookingClass)
                    .marketedBy(marketingCode)
                    .withPricingGroup(segmentNumber)
                    .departingAt(departureDate)
                .ok()
            .ok();
        }

        function witInfant(rb, infant) {
            return withFlight(
                rb.withInfant(infant.FirstName, infant.LastName)
            ).ok();
        }

        return builder;
    }

    function createViewRequest(flight, segmentNumber) {
        const {RetrieveRequestBuilder, PassengerType} = $window;

        const marketingCode = flight.MarketingCompanyCode || flight.MarketingAirlineCode;
        const flightNumber = parseFlightNumber(flight.FlightNumber, marketingCode);
        if (!flightNumber) {
            return;
        }
        const departureDate = moment(flight.DepartureDate).format(DATE_FORMAT);

        const arrivalAirportCode = flight.ArrivalAirportCode || flight.ArrivalAirport;
        const departureAirportCode = flight.DepartureAirportCode || flight.DepartureAirport;

        return new RetrieveRequestBuilder(flightNumber)
            .withPassenger('FirstName', 'LastName')
                .ofType(PassengerType.Adult)
                .withFlight()
                    .withSegment(departureAirportCode, arrivalAirportCode)
                        .withFareBase(flight.FareBasis)
                        .bookedWithClass(flight.BookingClass)
                        .marketedBy(marketingCode)
                        .withPricingGroup(segmentNumber)
                        .departingAt(departureDate)
                    .ok()
                .ok()
            .ok();
    }

    function parseFlightNumber(flightNumber, marketingCode) {
        return angular.isString(flightNumber)
            && marketingCode
            && flightNumber.substr(marketingCode.length);
    }

    function integrateClickingOnSeatElement(listener, map, $scope) {
        map.addEventListener(EVENT_SEAT_SELECTED, (seat) => {
            listener(seat);
            $scope.$apply();
        });
    }

    function selectSegmentAndDeck(segment, deck, map) {
        map.select(segment, deck);
        fixIeRenderingIssue();
    }

    function fixIeRenderingIssue() {
        setTimeout(() => { // eslint-disable-line
            const isIE = document.querySelector('.os-windows.browser-ie'); // eslint-disable-line
            if (!isIE) {
                return;
            }
            const element = document.querySelector('.seatmap-container'); // eslint-disable-line
            if (!element) {
                return;
            }
            element.style.height = '99%';
            setTimeout(() => element.style.height = '', 200); // eslint-disable-line
        }, 200); // eslint-disable-line
    }

    function getPassengers(map) {
        return angular.copy(map.passengers());
    }

    function getDeckSwitchers(map) {
        const segment = map.getSelectedSegmentObject();

        if (!segment || !segment.decks) {
            return {};
        }

        return segment.decks.map(({deckType}) => ({id: deckType, content: deckType}));
    }

    function integrateHoveringOnSeatElement(listenerHoverStart, listenerHoverEnd, map) {
        map.addEventListener(EVENT_SEAT_HOVERED_BEGIN, listenerHoverStart);
        map.addEventListener(EVENT_SEAT_HOVERED_END, listenerHoverEnd);
    }

    const REGEX_SELECTED_SEAT_CLASS = /\b(seat-picked-\d+)\b/i;
    const CLASS_SELECTED_SEAT = 'seat-picked';
    function showNumberOfEachTravellerOnSelectedSeats(map, flight, travellers, selectedSeats) {
        if (!map.target) {
            return;
        }
        const mapElement = map.target;
        const selectedSeatsElementPrevious = mapElement.querySelectorAll(`.${CLASS_SELECTED_SEAT}`);
        Array.prototype.forEach.call(selectedSeatsElementPrevious, removeNumberOnSeatElement);

        travellers.forEach((traveller, orderNumber) => {
            const isSeatSelected = selectedSeatsService.isSeatSelected(traveller, flight, selectedSeats);
            if (isSeatSelected) {
                const {SeatNumber: seatNumber} = selectedSeatsService.getSelectionModel(traveller, flight, selectedSeats);
                addNumberOnSeatElement(seatNumber, orderNumber);
            }
        });

        function removeNumberOnSeatElement(seat) {
            const classes = seat.classList.toString().match(REGEX_SELECTED_SEAT_CLASS);
            if (classes) {
                const seatClassName = classes[1];
                seat.classList.remove(seatClassName);
            }
            seat.classList.remove(CLASS_SELECTED_SEAT);
        }

        function addNumberOnSeatElement(seatNumber, index) {
            const seatElement = mapElement.querySelector(`.seat.seat-${seatNumber.toLowerCase()}`);
            if (!seatElement) {
                return;
            }
            const orderNumber = index + 1;
            seatElement.classList.add(CLASS_SELECTED_SEAT);
            seatElement.classList.add(`${CLASS_SELECTED_SEAT}-${orderNumber}`);
        }
    }

    function deselectOnOpenedSeatMap(selectedSeats, traveller, flight, map) {
        const isSeatSelected = selectedSeatsService.isSeatSelected(traveller, flight, selectedSeats);

        if (!isSeatSelected) {
            return;
        }

        if (map.selected) { //old map
            const prevSelectedPassenger = map.selected.passenger;

            map.selected.passenger = map.passengers()
                .reduce((acc, {firstName, surname}, index) =>
                        (firstName === traveller.FirstName && surname === traveller.LastName)
                            ? index
                            : acc,
                    null);

            const seat = selectedSeatsService.getSelectionModel(traveller, flight, selectedSeats);
            const mapElement = map.target;
            const seatElement = mapElement.querySelector(`.seat.seat-${seat.SeatNumber.toLowerCase()}`);
            if (seatElement) {
                seatElement.click();
            }
            selectedSeatsService.deselectSeat(selectedSeats, traveller, flight);
            map.selected.passenger = prevSelectedPassenger;
        } else { //new map
            let passenger;
            for (const entry of map.passengerList.entries()) {
                if (traveller.FirstName === entry[1].identity.firstName && traveller.LastName === entry[1].identity.lastName) {
                    passenger = entry[1];
                }
            }

            map.resetSeatSelectionForPassenger(passenger);
            selectedSeatsService.deselectSeat(selectedSeats, traveller, flight);
        }
    }

    function selectSeatsOnMap(selectedSeats, flight, travellers, map) {
        const mapElement = map.target;
        travellers.forEach((traveller, index) => {
            if (!selectedSeatsService.isSeatSelected(traveller, flight, selectedSeats)) {
                return;
            }
            const seat = selectedSeatsService.getSelectionModel(traveller, flight, selectedSeats);
            const seatElement = mapElement.querySelector(`.seat.seat-${seat.SeatNumber.toLowerCase()}:not(.selected)`);

            setTimeout(() => ((mapLocal, seatElementLocal) => {
                const prevSelectedPassenger = mapLocal.selected.passenger;
                mapLocal.selected.passenger = index;
                if (seatElementLocal) {
                    seatElementLocal.click();
                }
                mapLocal.selected.passenger = prevSelectedPassenger;
            })(map, seatElement), 0);

        });
    }

    function markSeatsAllowedToBeSelectedByTravellerWithInfant(map) {
        const MODE_ALL_SEATS_ALLOWED_BY_DEFAULT = 'mode-infant-all-seats-allowed';
        const MODE_ALL_SEATS_NOT_ALLOWED_BY_DEFAULT = 'mode-infant-all-seats-not-allowed';

        const mapElement = map.target;
        const isAllowedByDefault = isAllSeatAllowedByDefaultForTravellerWithInfant(map);

        if (isAllowedByDefault) {
            mapElement.classList.add(MODE_ALL_SEATS_ALLOWED_BY_DEFAULT);
            mapElement.classList.remove(MODE_ALL_SEATS_NOT_ALLOWED_BY_DEFAULT);
        } else {
            mapElement.classList.remove(MODE_ALL_SEATS_ALLOWED_BY_DEFAULT);
            mapElement.classList.add(MODE_ALL_SEATS_NOT_ALLOWED_BY_DEFAULT);
        }
    }

    function isAllSeatAllowedByDefaultForTravellerWithInfant(map) {
        const CLASS_SEAT_ALLOWED = SEAT_SUITABLE_FOR_UNUSAL_NEEDS.SUITABLE_FOR_ADULT_WITH_INFANT;
        const mapElement = map.target;
        return !mapElement.querySelector(`.characteristic-${CLASS_SEAT_ALLOWED}`);
    }
}



// WEBPACK FOOTER //
// ./src/app/modules/flights/flight-seatmap/amadeus-seatmap/amadeus-seatmap-service.js